import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import PdfViewerContainer from "../../../components/PdfViewerContainer";

const SELECT = "SELECT";
const FORM_NAME = "SBIPPF";
const MODIFY_ENDPOINT = "/backend/api/ajax_modify.php";
const PDF_ENDPOINT = "/ModifyMyForm/SBIPPF/PPFPrint.php";

const personalFields = [
  { name: "fabranch", label: "Branch", maxLength: 20, required: true },
  { name: "facifno", label: "CIF Number", maxLength: 11, numeric: true },
  { name: "faacno", label: "Account Number", maxLength: 11, numeric: true },
  { name: "faamount", label: "Subscription Amount", maxLength: 5, numeric: true, required: true },
  { name: "faname", label: "Applicant Name", maxLength: 33, alphaOnly: true, required: true },
  { name: "fadob", label: "Date of Birth", type: "date", required: true },
  { name: "fagender", label: "Gender", type: "select", required: true, options: [["MALE","MALE"],["FEMALE","FEMALE"]] },
  { name: "famstatus", label: "Marital Status", type: "select", required: true, options: [["MARRIED","MARRIED"],["UNMARRIED","UNMARRIED"]] },
  { name: "faaadhaarno", label: "Aadhaar Number", maxLength: 12, numeric: true, required: true },
  { name: "fapan", label: "PAN Number", maxLength: 10, uppercase: true },
  { name: "fafname", label: "Father's Name", maxLength: 33, alphaOnly: true, required: true },
  { name: "fasname", label: "Spouse Name", maxLength: 33, alphaOnly: true, required: true },
  { name: "famname", label: "Mother's Name", maxLength: 33, alphaOnly: true, required: true },
  { name: "fagname", label: "Guardian's Name", maxLength: 33, alphaOnly: true, required: true },
  { name: "fagrelation", label: "Guardian Relation", type: "select", required: true, options: [["FATHER","FATHER"],["MOTHER","MOTHER"],["BROTHER","BROTHER"],["SISTER","SISTER"],["OTHER","OTHER"]] },
];

const addressFields = [
  { name: "faadd1", label: "Address Line 1", maxLength: 36, required: true },
  { name: "faadd2", label: "Address Line 2", maxLength: 36, required: true },
  { name: "facity", label: "City", maxLength: 15, required: true },
  { name: "fadist", label: "District", maxLength: 15, required: true },
  { name: "fastate", label: "State", maxLength: 36, required: true },
  { name: "fapin", label: "PIN", maxLength: 6, numeric: true, required: true },
  { name: "famobile", label: "Mobile", maxLength: 10, numeric: true, required: true },
  { name: "faincome", label: "Income", maxLength: 8, numeric: true, required: true },
  { name: "fareligion", label: "Religion", type: "select", required: true, options: [["HINDU","HINDU"],["MUSLIM","MUSLIM"],["CHRISTIAN","CHRISTIAN"],["SIKH","SIKH"]] },
  { name: "facategory", label: "Category", type: "select", required: true, options: [["GENERAL","GENERAL"],["OBC","OBC"],["SC","SC"],["ST","ST"]] },
  { name: "faqualification", label: "Qualification", type: "select", required: true, options: [["BSSC","BELOW SSC"],["SSC","SSC"],["HSC","HSC"],["GRADUATE","GRADUATE"],["PGRADUATE","POST GRADUATE"],["PROFESSIONAL","PROFESSIONAL"]] },
  { name: "faoccupation", label: "Occupation", type: "select", required: true, options: [["SERVICE","SERVICE"],["PRIVATE_SECTOR","PRIVATE SECTOR"],["PUBLIC_SECTOR","PUBLIC SECTOR"],["GOVERNMENT_SECTOR","GOVERNMENT SECTOR"],["OTHERS","OTHERS"],["PROFESSIONAL","PROFESSIONAL"],["SELF_EMPLOYED","SELF EMPLOYED"],["RETIRED","RETIRED"],["HOUSEWIFE","HOUSEWIFE"],["STUDENT","STUDENT"],["BUSINESS","BUSINESS"]] },
];

const allFields = [...personalFields, ...addressFields];
const tabs = ["Personal Details", "Address & Profile", "Preview"];
const initialFormData = Object.fromEntries(allFields.map((f) => [f.name, f.type === "select" ? SELECT : ""]));

function ageFromDob(value) {
  if (!value) return null;
  const dob = new Date(`${value}T00:00:00`);
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;
  return age;
}

function optionLabel(field, value) {
  if (!field.options || value === SELECT || !value) return "-";
  return field.options.find(([v]) => v === value)?.[1] || value;
}

function formatDate(value) {
  if (!value) return "-";
  const [y,m,d] = value.split("-");
  return y && m && d ? `${d}/${m}/${y}` : value;
}

async function readJson(response) {
  const text = await response.text();
  if (!text.trim()) return null;
  try { return JSON.parse(text); } catch { throw new Error("The server returned invalid JSON."); }
}

export default function SBIPPF() {
  const location = useLocation();
  const navigate = useNavigate();

  const refno = String(
    location.state?.refno ||
    new URLSearchParams(location.search).get("refno") ||
    ""
  ).trim();

  const [loadingExisting, setLoadingExisting] = useState(Boolean(refno));
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [currentTab, setCurrentTab] = useState(0);
  const [maxAllowedTab, setMaxAllowedTab] = useState(0);
  const [pdfUrl, setPdfUrl] = useState("");
  const [showPdf, setShowPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const nextButtonRef = useRef(null);

  const age = useMemo(() => ageFromDob(formData.fadob), [formData.fadob]);
  const isMinor = age !== null && age < 18;
  const currentFields = currentTab === 0 ? personalFields : currentTab === 1 ? addressFields : [];

  const disabled = (field) =>
    (field.name === "fasname" && formData.famstatus === "UNMARRIED") ||
    (["fagname","fagrelation"].includes(field.name) && !isMinor);

  useEffect(() => {
    const first = currentFields.find((field) => !disabled(field));
    if (!first) return;
    const id = requestAnimationFrame(() => document.getElementById(first.name)?.focus());
    return () => cancelAnimationFrame(id);
  }, [currentTab, formData.famstatus, isMinor]);

  useEffect(() => {
    if (formData.famstatus === "UNMARRIED") {
      setFormData((p) => ({ ...p, fasname: "" }));
      setErrors((p) => ({ ...p, fasname: "" }));
    }
  }, [formData.famstatus]);

  useEffect(() => {
    if (!isMinor) {
      setFormData((p) => ({ ...p, fagname: "", fagrelation: SELECT }));
      setErrors((p) => ({ ...p, fagname: "", fagrelation: "" }));
    }
  }, [isMinor]);

  useEffect(() => {
    if (!refno) {
      setLoadingExisting(false);
      return;
    }

    const controller = new AbortController();

    (async () => {
      try {
        const body = new FormData();
        body.append("action", "get");
        body.append("form_name", FORM_NAME);
        body.append("refno", refno);

        const response = await fetch(MODIFY_ENDPOINT, {
          method: "POST",
          body,
          credentials: "include",
          cache: "no-store",
          headers: { Accept: "application/json" },
          signal: controller.signal,
        });

        const result = await readJson(response);

        if (!response.ok || !result?.success || !result?.data) {
          throw new Error(result?.message || "Unable to load the saved form.");
        }

        const data = result.data;

        setFormData((previous) => {
          const next = { ...previous };

          allFields.forEach((field) => {
            if (Object.prototype.hasOwnProperty.call(data, field.name)) {
              let value = data[field.name] ?? "";

              if (field.type === "select" && value === "") {
                value = SELECT;
              }

              next[field.name] = String(value);
            }
          });

          return next;
        });

        setMaxAllowedTab(2);
        setCurrentTab(0);
      } catch (error) {
        if (error.name !== "AbortError") {
          setPdfError(error.message || "Unable to load the saved form.");
        }
      } finally {
        setLoadingExisting(false);
      }
    })();

    return () => controller.abort();
  }, [refno]);

  useEffect(() => {
    if (refno) return;

    const controller = new AbortController();
    (async () => {
      try {
        const response = await fetch(`/backend/api/autofill.php?form=${encodeURIComponent(FORM_NAME)}`, {          credentials: "include", cache: "no-store", headers: { Accept: "application/json" }, signal: controller.signal,
        });
        const result = await readJson(response);
        if (!response.ok || !result?.success || !result?.found || !result?.data) return;
        setFormData((p) => ({
          ...p,
          fabranch: p.fabranch || result.data.branch || "",
          facity: p.facity || result.data.city || "",
          fadist: p.fadist || result.data.dist || "",
          fastate: p.fastate || result.data.state || "",
          fapin: p.fapin || result.data.pin || "",
        }));
      } catch (error) {
        if (error.name !== "AbortError") console.error("SBIPPF autofill error:", error);
      }
    })();
    return () => controller.abort();
  }, []);

  useEffect(() => () => {
    if (pdfUrl?.startsWith("blob:")) URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  const updateField = (field, raw) => {
    let value = raw;

    if (field.numeric) {
      value = value.replace(/\D/g, "");
    }

    if (field.alphaOnly) {
      value = value.replace(/[^A-Za-z ]/g, "");
    }

    if (field.uppercase || field.name.includes("pan")) {
      value = value.toUpperCase();
    }

    setFormData((p) => ({ ...p, [field.name]: value }));
    setErrors((p) => ({ ...p, [field.name]: "" }));
  };

  const validateField = (field) => {
    if (disabled(field)) return "";
    const value = String(formData[field.name] ?? "").trim();

    if (field.required && (field.type === "select" ? value === SELECT || !value : !value)) {
      return `${field.label} is required.`;
    }
    if (field.alphaOnly && value && !/^[A-Za-z ]+$/.test(value)) {
      return `${field.label} must contain alphabet letters only.`;
    }

    if (field.name === "facifno" && value && !/^\d{11}$/.test(value)) {
      return "CIF number must contain 11 digits.";
    }

    if (field.name === "faacno" && value && !/^\d{11}$/.test(value)) {
      return "Account number must contain 11 digits.";
    }

    if (field.name === "faaadhaarno" && value && !/^\d{12}$/.test(value)) {
      return "Aadhaar number must contain exactly 12 digits.";
    }

    if (field.name === "fapin" && value && !/^\d{6}$/.test(value)) {
      return "PIN must contain exactly 6 digits.";
    }

    if (field.name === "famobile" && value && !/^\d{10}$/.test(value)) {
      return "Mobile number must contain 10 digits.";
    }

    if (field.name === "fapan" && value && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(value)) {
      return "Enter a valid PAN number (e.g. ABCDE1234F).";
    }
    return "";
  };

  const validate = (fields) => {
    const next = {};
    fields.forEach((field) => {
      const message = validateField(field);
      if (message) next[field.name] = message;
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const goNext = () => {
    if (!validate(currentFields)) return;
    const next = currentTab + 1;
    setCurrentTab(next);
    setMaxAllowedTab((p) => Math.max(p, next));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goPrevious = () => {
    setCurrentTab((p) => Math.max(0, p - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLastFieldKeyDown = (event, fieldName) => {
    const enabledFields = currentFields.filter((field) => !disabled(field));
    const last = enabledFields[enabledFields.length - 1]?.name;
    if (fieldName === last && event.key === "Tab" && !event.shiftKey) {
      event.preventDefault();
      nextButtonRef.current?.focus();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate(allFields)) {
      const firstInvalid = allFields.find((f) => validateField(f));
      setCurrentTab(personalFields.some((f) => f.name === firstInvalid?.name) ? 0 : 1);
      return;
    }

    setPdfLoading(true);
    setPdfError("");

    try {
      const body = new FormData();

      Object.entries(formData).forEach(([name, value]) =>
        body.append(name, value === SELECT ? "" : String(value ?? ""))
      );

      body.append("FORM_NAME", FORM_NAME);

      if (refno) {
        body.append("refno", refno);
      }

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 60000);
      let response;
      try {
        response = await fetch(PDF_ENDPOINT, {
          method: "POST", body, credentials: "include", headers: { Accept: "application/pdf" }, signal: controller.signal,
        });
      } finally {
        clearTimeout(timeout);
      }

      if (!response.ok) throw new Error((await response.text()) || `PDF generation failed (${response.status}).`);
      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/pdf")) throw new Error((await response.text()) || "Print.php did not return a PDF file.");

      const url = URL.createObjectURL(await response.blob());
      setPdfUrl((old) => {
        if (old?.startsWith("blob:")) URL.revokeObjectURL(old);
        return url;
      });
      setShowPdf(true);
    } catch (error) {
      setPdfError(error.name === "AbortError" ? "PDF generation timed out." : error.message || "Unable to generate PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  const renderField = (field) => {
    const isDisabled = disabled(field);
    const error = errors[field.name];

    return (
      <div key={field.name} className="space-y-2">
        <Label htmlFor={field.name}>
          {field.label}{field.required && !isDisabled && <span className="text-red-500"> *</span>}
        </Label>

        {field.type === "select" ? (
          <select
            id={field.name}
            name={field.name}
            value={formData[field.name]}
            disabled={isDisabled}
            onChange={(e) => updateField(field, e.target.value)}
            onKeyDown={(e) => handleLastFieldKeyDown(e, field.name)}
            className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50 ${error ? "border-red-500" : ""}`}
          >
            <option value={SELECT}>SELECT</option>
            {field.options.map(([value,label]) => <option key={`${field.name}-${value}`} value={value}>{label}</option>)}
          </select>
        ) : (
          <Input
            id={field.name}
            name={field.name}
            type={field.type || "text"}
            value={formData[field.name]}
            maxLength={field.maxLength}
            disabled={isDisabled}
            placeholder={field.label}
            className={error ? "border-red-500" : ""}
            onChange={(e) => updateField(field, e.target.value)}
            onKeyDown={(e) => handleLastFieldKeyDown(e, field.name)}
          />
        )}

        {field.name === "fadob" && age !== null && (
          <p className="text-sm text-muted-foreground">Age: {age} years{isMinor ? " — Guardian details required." : ""}</p>
        )}
        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    );
  };

  if (loadingExisting) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="mx-auto max-w-3xl">
          <Card>
            <CardContent className="p-8 text-center">
              Loading saved SBI PPF form...
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (showPdf && pdfUrl) {
    return <PdfViewerContainer pdfUrl={pdfUrl} title="SBI PPF Account Opening Form" onBack={() => setShowPdf(false)} />;
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/ModifyMyForm/SBIPPF")}
          >
            ← Back to Saved Forms
          </Button>
        </div>
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-3xl">✏️ Modify SBI PPF Account Opening Form</CardTitle>
            <p className="text-muted-foreground">Reference No.: {refno}</p>
          </CardHeader>
        </Card>

        <div className="mb-8 grid grid-cols-3 gap-3">
          {tabs.map((tab,index) => (
            <Button key={tab} type="button" variant={currentTab === index ? "default" : "outline"} disabled={index > maxAllowedTab} onClick={() => setCurrentTab(index)}>
              {index + 1}. {tab}
            </Button>
          ))}
        </div>

        {currentTab < 2 ? (
          <Card>
            <CardHeader>
              <CardTitle>{currentTab === 0 ? "👤 Applicant Personal Details" : "🏠 Address and Profile Details"}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {currentFields.map(renderField)}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader><CardTitle>📋 Preview</CardTitle></CardHeader>
            <CardContent className="space-y-8">
              {[["Personal Details",personalFields],["Address and Profile Details",addressFields]].map(([title,fields]) => (
                <section key={title}>
                  <h3 className="mb-3 text-lg font-semibold">{title}</h3>
                  <div className="overflow-hidden rounded-lg border">
                    {fields.map((field) => (
                      <div key={field.name} className="grid grid-cols-2 border-b p-3 last:border-b-0">
                        <span className="font-medium">{field.label}</span>
                        <span className="break-words">
                          {field.type === "date" ? formatDate(formData[field.name]) : field.type === "select" ? optionLabel(field, formData[field.name]) : formData[field.name] || "-"}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </CardContent>
          </Card>
        )}

        {pdfError && <p className="mt-4 rounded-lg border border-red-300 bg-red-50 p-3 text-red-700">{pdfError}</p>}

        <div className="mt-6 flex items-center justify-between">
          <Button type="button" variant="outline" onClick={goPrevious} disabled={currentTab === 0}>← Previous</Button>
          {currentTab === 2 ? (
            <Button type="button" ref={nextButtonRef} onClick={handleSubmit} disabled={pdfLoading}>
              {pdfLoading ? "Generating PDF..." : "Submit & Preview PDF"}
            </Button>
          ) : (
            <Button type="button" ref={nextButtonRef} onClick={goNext}>
              {currentTab === 1 ? "Preview" : "Next →"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
